<template>
  <div data-testid="credit-packs-table">
    <data-table
      ref="datatableRef"
      test-id-prefix="credit-packs"
      :config="tableConfig"
      :data="rows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @button-click="handleButtonClick"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatCredits } from '../../../assistant/formatCredits'
import { log } from '../../../../utils/DebugLogger'
import { useCreditPacks } from '../../../../composables/useCreditPacks'
import { useAiSettings } from '../../../../composables/useAiSettings'
import { MICRO_PER_CREDIT } from '../../../../composables/useCreditWallet'
import { computeMarginIdentity, formatMarginMultiple, type MarginIdentityResult } from '../creditPricing'
import type { AiCredits_PackRow } from '../../../../contracts/AiCredits_PackRow'

const { t } = useI18n()

interface Props {
  rows: AiCredits_PackRow[]
}

defineProps<Props>()

const emit = defineEmits<{
  edit: [row: AiCredits_PackRow]
  'toggle-active': [row: AiCredits_PackRow]
  refresh: []
}>()

interface DataTableExposed {
  loadData: () => void
}

const datatableRef = ref<DataTableExposed | null>(null)

// reserveFloorMicro + peg are read here (not passed as props) so the derived
// columns below can compute against them without StaffCreditsPage having to
// thread two more props through — both are module-scope singletons already
// used elsewhere in this domain (useCreditPacks, useAiSettings).
const { reserveFloorMicro, fetchReserveFloorMicro } = useCreditPacks()
const { peg, loadPeg } = useAiSettings()

onMounted(() => {
  if (peg.value == null) {
    loadPeg().catch((err) => {
      log('CreditPacksTable', 'onMounted', 'Erro ao carregar peg', { message: (err as Error)?.message })
    })
  }
  if (reserveFloorMicro.value == null) {
    fetchReserveFloorMicro().catch((err) => {
      log('CreditPacksTable', 'onMounted', 'Erro ao carregar piso de reserva', { message: (err as Error)?.message })
    })
  }
})

/**
 * usdMarginIdentityOrNull — the §2.4 identity for a row, via the SAME shared
 * computeMarginIdentity() the Calculadora tab calls (creditPricing.ts) — never
 * re-derived here. Only meaningful for a USD price row — a BRL/CLP row has no
 * display FX to convert through, so this returns null for anything that isn't
 * literally currency === 'USD'. `peg.value` may still be unloaded (null) when
 * this runs; retailPricePerCreditUsd never depends on the peg, only
 * marginMultiple does (computeMarginIdentity itself returns null for that when
 * pegUsd is not positive).
 */
function usdMarginIdentityOrNull(row: AiCredits_PackRow): MarginIdentityResult | null {
  if (row.currency !== 'USD' || row.price_cents == null || !row.credits) return null
  const pegRow = peg.value
  const pegUsd = pegRow != null ? parseFloat(pegRow.ai_credit_peg_usd) : 0
  return computeMarginIdentity({
    retailPriceUsd: row.price_cents / 100,
    packSizeCredits: row.credits / MICRO_PER_CREDIT,
    pegUsd,
  })
}

function fmtUsdPerCredit(v: number): string {
  return '$ ' + v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
}

const tableConfig = computed(() => ({
  tableId: 'creditPacksTable',
  title: t('Pacotes de Créditos'),
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
    },
    {
      data: 'name',
      title: t('Nome'),
      sortable: true,
    },
    {
      data: 'credits',
      title: t('Créditos'),
      sortable: true,
      cellRenderer: ({ value }: { value: unknown }) =>
        `${formatCredits(value as number)} cr`,
    },
    {
      data: 'retail_usd_per_credit',
      title: t('USD por crédito (retail)'),
      sortable: false,
      cellRenderer: ({ data }: { data: Record<string, unknown> }) => {
        const row = data as unknown as AiCredits_PackRow
        if (row.currency == null) return t('— (sem preço cadastrado)')
        const identity = usdMarginIdentityOrNull(row)
        if (identity == null) return `${t('— (preço apenas em')} ${row.currency})`
        return fmtUsdPerCredit(identity.retailPricePerCreditUsd)
      },
    },
    {
      data: 'margin_multiple',
      title: t('Múltiplo de margem'),
      sortable: false,
      // §2.4: margin_multiple = retail_price_per_credit_usd / peg_usd — via the
      // shared computeMarginIdentity(), same as the Calculadora tab.
      cellRenderer: ({ data }: { data: Record<string, unknown> }) => {
        const row = data as unknown as AiCredits_PackRow
        const identity = usdMarginIdentityOrNull(row)
        if (identity == null) return '—'
        if (peg.value == null) return '…'
        return formatMarginMultiple(identity.marginMultiple)
      },
    },
    {
      data: 'estimated_prompts',
      title: t('Perguntas estimadas'),
      sortable: false,
      // §2.5 anchor: 1 credito ~ 1 pergunta típica — leitura direta, não uma promessa.
      cellRenderer: ({ data }: { data: Record<string, unknown> }) => {
        const row = data as unknown as AiCredits_PackRow
        const prompts = row.credits / MICRO_PER_CREDIT
        return `~${prompts.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${t('perguntas (estimativa)')}`
      },
    },
    {
      data: 'reserve_floor_flag',
      title: t('Piso de reserva'),
      sortable: false,
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => {
        const r = row as unknown as AiCredits_PackRow
        const floor = reserveFloorMicro.value
        if (floor == null) {
          return { label: '…', severity: 'secondary' }
        }
        if (r.credits < floor) {
          return {
            label: t('Abaixo do piso'),
            severity: 'danger',
            tooltip: t('Este pacote não cobre o pior caso de uma única mensagem'),
          }
        }
        return { label: t('OK'), severity: 'success' }
      },
    },
    {
      data: 'active',
      title: t('Ativo'),
      sortable: true,
      renderType: 'active_inactive',
    },
    {
      data: 'sort_order',
      title: t('Ordem'),
      sortable: true,
    },
    {
      data: 'id',
      title: t('Editar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Editar'),
      buttonIcon: 'pi pi-pencil',
      buttonAction: 'edit',
      buttonTestId: (row: Record<string, unknown>) =>
        `credit-packs-edit-row-${row.id as number}`,
    },
    {
      data: 'active',
      title: t('Ativar/Desativar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ativar/Desativar'),
      buttonIcon: 'pi pi-power-off',
      buttonAction: 'toggle-active',
      buttonTestId: (row: Record<string, unknown>) =>
        `credit-packs-toggle-row-${row.id as number}`,
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) =>
    row?.id != null ? `credit-packs-row-${row.id as number}` : '',
}))

function handleButtonClick(e: {
  value: unknown
  data: AiCredits_PackRow
  action: string
}): void {
  if (e.action === 'edit') {
    emit('edit', e.data)
  } else if (e.action === 'toggle-active') {
    emit('toggle-active', e.data)
  }
}
</script>
